import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ApiError, api } from '../api/client';
import { ARTICLES, articleFor, renderArticle, visibleTo, type HelpArticle } from '../help';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { Button, Drawer, Notice } from './ui';

/** Who is reading help: decides which articles they see. */
export function useHelpRole(): 'staff' | 'admin' | 'user' | null {
  const { state } = useAuth();
  if (!state?.signedIn) return null;
  if (state.user?.staff) return 'staff';
  return (state.memberships ?? []).some((m) => m.role === 'admin') ? 'admin' : 'user';
}

/** The company in the address, if the person is inside one. */
function tenantIn(path: string): string | undefined {
  const m = /^\/t\/([^/]+)/.exec(path);
  return m ? decodeURIComponent(m[1]!) : undefined;
}

/** An article's text and pictures. Links to other articles open them in the help center. */
export function ArticleBody({ article, onNavigate, autoFocus }: { article: HelpArticle; onNavigate?: () => void; autoFocus?: boolean }) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  // A related guide opened in the panel starts at its top too.
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a[data-help-link]');
    if (!a) return;
    e.preventDefault();
    onNavigate?.();
    navigate(a.getAttribute('href') ?? '/help');
  };
  // The article text is Stitch's own, from the code, with any raw HTML escaped.
  // In the Help panel the guide takes focus, so it opens at the top and screen readers start there.
  return (
    <div
      ref={ref}
      className="help-article"
      onClick={onClick}
      {...(autoFocus ? { tabIndex: -1, 'data-autofocus': '' } : {})}
      dangerouslySetInnerHTML={{ __html: renderArticle(article) }}
    />
  );
}

/** "Still stuck?": a message to Stitch support, and how else to reach them. */
export function SupportBox({ compact }: { compact?: boolean }) {
  const { state } = useAuth();
  const { pathname } = useLocation();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState('');
  const support = state?.support ?? {};
  const signedIn = Boolean(state?.signedIn);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const tenant = tenantIn(pathname);
      const r = await api.askSupport({ message: message.trim(), page: pathname.startsWith('/help') ? '/help' : pathname, ...(tenant ? { tenant } : {}) });
      setSent(r.replyTo);
      setMessage('');
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t send. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`support-box${compact ? ' compact' : ''}`} aria-labelledby="support-title">
      <div className="support-head">
        <span className="support-icon" aria-hidden="true">
          <Icon name="lifebuoy" size={18} />
        </span>
        <h2 id="support-title">Still stuck? Stitch support can help</h2>
      </div>
      {sent ? (
        <Notice tone="ok" title="We’ve got your message">
          Someone from Stitch will reply to {sent} soon.{support.phone ? ` If it’s urgent, call us on ${support.phone}.` : ''}
        </Notice>
      ) : signedIn ? (
        <form onSubmit={send} className="support-form">
          <label htmlFor="support-message" className="field-label">
            What do you need help with?
          </label>
          <textarea
            id="support-message"
            className="input textarea"
            rows={compact ? 3 : 4}
            maxLength={2000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="For example: calls to our main number aren’t reaching the front desk."
          />
          <span className="hint">We’ll see which screen you were on, so you don’t need to explain where.</span>
          {error && <Notice tone="crit">{error}</Notice>}
          <div className="support-actions">
            <Button variant="primary" type="submit" icon="send" disabled={busy || message.trim().length < 3}>
              {busy ? 'Sending…' : 'Send to Stitch support'}
            </Button>
          </div>
        </form>
      ) : (
        <p className="muted">Sign in to send us a message from here.</p>
      )}
      {(support.email || support.phone) && (
        <p className="support-other">
          {support.phone && (
            <a href={`tel:${support.phone.replace(/[^\d+]/g, '')}`}>
              <Icon name="phone" size={15} /> {support.phone}
            </a>
          )}
          {support.email && (
            <a href={`mailto:${support.email}`}>
              <Icon name="mail" size={15} /> {support.email}
            </a>
          )}
        </p>
      )}
    </section>
  );
}

/** The help for the screen someone is on, beside it, with a way to ask Stitch. */
export function HelpDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  const role = useHelpRole();
  const here = articleFor(pathname);
  const [picked, setPicked] = useState<string | null>(null);
  const article = (picked ? ARTICLES.find((a) => a.id === picked) : undefined) ?? here;
  const others = ARTICLES.filter((a) => visibleTo(a, role) && a.id !== article?.id && a.section === article?.section).slice(0, 4);

  return (
    <Drawer
      open={open}
      onClose={() => {
        setPicked(null);
        onClose();
      }}
      title={article ? article.title : 'Help'}
      subtitle={article ? article.summary : 'Guides to everything in Stitch'}
      foot={
        <Link className="btn secondary" to={article ? `/help/${article.id}` : '/help'} onClick={onClose}>
          <Icon name="expand" size={16} /> Open in the help center
        </Link>
      }
    >
      {article ? <ArticleBody key={article.id} article={article} onNavigate={onClose} autoFocus /> : <p className="muted">There’s no guide for this screen yet. The help center has everything else.</p>}
      {others.length > 0 && (
        <nav className="help-related" aria-label="Related guides">
          <span className="help-related-title">Related</span>
          {others.map((a) => (
            <button key={a.id} type="button" className="link-btn" onClick={() => setPicked(a.id)}>
              {a.title}
            </button>
          ))}
        </nav>
      )}
      <SupportBox compact />
    </Drawer>
  );
}

/** The Help button at the top of every screen. */
export function HelpButton({ compact }: { compact?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {compact ? (
        <button type="button" className="icon-btn" aria-label="Help" title="Help" onClick={() => setOpen(true)} aria-haspopup="dialog">
          <Icon name="help" size={20} />
        </button>
      ) : (
        <Button variant="ghost" icon="help" onClick={() => setOpen(true)} aria-haspopup="dialog">
          Help
        </Button>
      )}
      {open && <HelpDrawer open onClose={() => setOpen(false)} />}
    </>
  );
}
